import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CreditCard, Clock } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface SubscriptionGuardProps {
  children: React.ReactNode;
}

export const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({ children }) => {
  const { 
    user, 
    organizationId, 
    loading: authLoading, 
    subscriptionStatus, 
    hasValidSubscription,
    subscriptionLoading 
  } = useAuth();

  // DEBUG: Log all values to console
  console.log('🔍 SubscriptionGuard DEBUG:', {
    organizationId,
    subscriptionStatus,
    hasValidSubscription,
    authLoading,
    subscriptionLoading,
    user: user?.id
  });

  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);

  const handleStartTrial = () => {
    // Navigate to business trial signup
    window.location.href = '/business-trial';
  };

  const handleResumeCheckout = async () => {
    if (!user || !organizationId) {
      toast.error('Missing user or organization data. Redirecting to signup...');
      window.location.href = '/business-trial';
      return;
    }

    setIsCreatingCheckout(true);
    
    try {
      // Call create-checkout function with existing organization data
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          priceId: 'price_1RifjIL45zl3J0lpsFeupPtl', // Business trial price ID
          organizationId: organizationId,
          successUrl: `${window.location.origin}/checkout-success?setup=new`,
          cancelUrl: `${window.location.origin}/business-trial`,
          trialDays: 14,
          allowPromoCodes: true,
          metadata: {
            user_id: user.id,
            user_email: user.email,
            resume_trial: 'true'
          }
        }
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data?.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      toast.error('Failed to resume trial setup. Please try again or contact support.');
      setIsCreatingCheckout(false);
    }
  };

  // Show loading while checking auth or subscription status
  if (authLoading || subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#FA8C00]" />
          <p className="mt-2 text-gray-600">
            {authLoading ? "Authenticating..." : "Loading Hivetivity..."}
          </p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!user) {
    // Check if we're on marketing domain and need to redirect to app domain
    if (typeof window !== 'undefined' && !window.location.hostname.includes('app.')) {
      // On marketing domain - redirect to app domain for auth
      const appUrl = import.meta.env.DEV 
        ? (import.meta.env.VITE_SITE_URL || 'http://localhost:5173')
        : (import.meta.env.VITE_APP_URL || 'https://app.hivetivity.com');
      window.location.href = appUrl + '/auth';
      return null;
    } else {
      // Already on app domain, use normal navigation
      return <Navigate to="/auth" replace />;
    }
  }

  // User has valid subscription - allow access
  if (hasValidSubscription) {
    return <>{children}</>;
  }

  // Show subscription gate for trial_pending, no_subscription, or expired
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-[#FA8C00]/10 flex items-center justify-center">
            {subscriptionStatus === 'trial_pending' ? (
              <Clock className="h-6 w-6 text-[#FA8C00]" />
            ) : (
              <CreditCard className="h-6 w-6 text-[#FA8C00]" />
            )}
          </div>
          <CardTitle className="text-xl">
            {subscriptionStatus === 'trial_pending' 
              ? 'Finish Setting Up Your Trial'
              : 'Subscription Required'
            }
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center text-gray-600">
            {subscriptionStatus === 'trial_pending' && (
              <p>Complete your checkout to start your 14-day free trial and access the app.</p>
            )}
            {subscriptionStatus === 'expired' && (
              <p>Your trial has expired. Choose a plan to continue using the app.</p>
            )}
            {(!subscriptionStatus || subscriptionStatus === 'no_subscription') && (
              <p>Start your free trial to access all features.</p>
            )}
          </div>

          <div className="space-y-2">
            {subscriptionStatus === 'trial_pending' ? (
              // Only show "Complete Trial Setup" button for trial_pending users
              <Button 
                onClick={handleResumeCheckout}
                disabled={isCreatingCheckout}
                className="w-full bg-[#FA8C00] hover:bg-[#FA8C00]/90"
              >
                {isCreatingCheckout ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating checkout...
                  </>
                ) : (
                  'Complete Trial Setup'
                )}
              </Button>
            ) : (
              // Show "Start Free Trial" for other statuses
              <Button 
                onClick={handleStartTrial}
                className="w-full bg-[#FA8C00] hover:bg-[#FA8C00]/90"
              >
                Start Free Trial
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionGuard;